import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { useTranslation } from "@/hooks/useTranslation";

function LoginPage({ isModal = false, onSwitchToRegister = null, onClose = null }) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isFirstSetup, setIsFirstSetup] = useState(false);

  // Redirect if already logged in (only when not in modal mode)
  useEffect(() => {
    if (user && !isModal) {
      navigate(`/${locale}/`);
    }
  }, [user, navigate, isModal]);

  const handleSubmit = async (e) => {
    console.log('[LoginPage] handleSubmit CALLED, loading:', loading, 'show2FA:', show2FA);
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const result = await login(email, password, show2FA ? totpCode : null);
      console.log('[LoginPage] handleSubmit result:', JSON.stringify(result));
      console.log('[LoginPage] result object:', result);
      
      // Check if 2FA is required
      if (result.requires2FA) {
        console.log('[LoginPage] 2FA REQUIRED - showing 2FA form');
        setShow2FA(true);
        setTempToken(result.tempToken);
        setQrCode(result.qrCode);
        setIsFirstSetup(result.isFirstSetup);
        return;
      }
      
      if (result.success) {
        toast.success(t('auth.login.success'));
        if (isModal && onClose) {
          onClose();
        } else {
          navigate(`/${locale}/`);
        }
      } else {
        toast.error(result.error || t('auth.login.error_credentials'));
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(t('auth.login.error_generic'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle 2FA code submission
  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error(t('auth.login.error_2fa_code_required'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(email, password, totpCode);
      
      if (result.success) {
        toast.success(t('auth.login.success'));
        setShow2FA(false);
        if (isModal && onClose) {
          onClose();
        } else {
          navigate(`/${locale}/`);
        }
      } else {
        toast.error(result.error || t('auth.login.error_2fa_wrong'));
      }
    } catch (error) {
      console.error("2FA error:", error);
      toast.error(t('auth.login.error_2fa_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-4xl font-bold text-[#FFDD59] cursor-pointer hover:opacity-80 transition-opacity" style={{ fontFamily: 'Poppins, sans-serif' }}>
              OPARA
            </h1>
          </Link>
        </div>

        {/* Login Card */}
        <Card className="bg-gray-900 border-2 border-gray-800">
          <CardContent className="p-8">
            {/* 2FA Form */}
            {show2FA ? (
              <div>
                <div className="text-center mb-6">
                  <ShieldCheck className="w-16 h-16 text-[#FFDD59] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('auth.twofa.title')}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {isFirstSetup
                      ? t('auth.twofa.desc_setup')
                      : t('auth.twofa.desc_normal')}
                  </p>
                </div>
                
                {/* QR Code for first setup */}
                {isFirstSetup && qrCode && (
                  <div className="mb-6 p-4 bg-white rounded-lg flex justify-center">
                    <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}
                
                <form onSubmit={handle2FASubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth.twofa.code_label')}
                    </label>
                    <Input
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-widest"
                      autoFocus
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold py-3"
                    disabled={loading || totpCode.length !== 6}
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t('auth.twofa.verify_btn')
                    )}
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShow2FA(false);
                      setTotpCode("");
                    }}
                    className="w-full text-gray-400 hover:text-white text-sm mt-2"
                  >
                    {t('auth.twofa.back_btn')}
                  </button>
                </form>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('auth.login.title')}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth.login.email_label')}
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.login.password_label')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    minLength={6}
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Forgot Password Link */}
                <div className="text-right mt-2">
                  <Link 
                    to={locale === 'tr' ? "/tr/sifremi-unuttum" : "/forgot-password"} 
                    className="text-sm text-[#FFDD59] hover:text-[#E6C84D] transition-colors"
                  >
                    {t('auth.login.forgot_password')}
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6 mt-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('auth.login.loading')}
                  </>
                ) : (
                  t('auth.login.submit_btn')
                )}
              </Button>
            </form>

            {/* Register Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                {t('auth.login.no_account')}{" "}
                <Link to={locale === 'tr' ? "/tr/kayit-ol" : "/register"} className="text-[#FFDD59] hover:underline font-semibold">
                  {t('auth.login.register_link')}
                </Link>
              </p>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Footer - Same across all pages */}
      <Footer />
      
      {/* Language Sidebar - Vertical on right side */}
      {/* Language Sidebar removed */}
    </div>
  );
}

export default LoginPage;
